import { AfterContentChecked, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { marker } from '@common/translate-marker';
import { GeneralActCargoTypeCodes } from '@common/knownTypes/GeneralActCargoTypeCodes';
import { GeneralActService } from '@common/services/general-act.service';
import { maxLengthValidator } from '@common/validators/max-length-validator';
import { requiredValidator } from '@common/validators/required-validator';
import { TranslateService } from '@ngx-translate/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { ToastService } from '@services/toast.service';
import { GeneralActItemDisplayModel } from '../../models/GeneralAct.display.model';

@Component({
    selector: 'app-general-act-manual-correction-modal',
    templateUrl: './general-act-manual-correction-modal.component.html',
    standalone: false
})
export class GeneralActManualCorrectionModalComponent implements OnInit, AfterContentChecked {
    public title: string;
    public id: number;
    public cargoTypeId: string;
    public cargoDirectionId: string;
    public approvedCustomsApprovalOverrideConfirmed = false;
    public form: FormGroup;
    public isBusy = false;
    public item: GeneralActItemDisplayModel;

    get forContainers(): boolean {
        return this.cargoTypeId === GeneralActCargoTypeCodes.containers;
    }

    get forBulk(): boolean {
        return this.cargoTypeId === GeneralActCargoTypeCodes.bulk;
    }

    get forRoro(): boolean {
        return this.cargoTypeId === GeneralActCargoTypeCodes.roro;
    }

    get showWeightInVacuum(): boolean {
        return (
            this.forBulk &&
            ((this.form?.get('declaredWeightInVacuum')?.value ?? 0) > 0 ||
                (this.form?.get('realizedWeightInVacuum')?.value ?? 0) > 0 ||
                (this.item?.declaredWeightInVacuum ?? 0) > 0 ||
                (this.item?.realizedWeightInVacuum ?? 0) > 0)
        );
    }

    constructor(
        public activeModal: DialogRef,
        private generalActService: GeneralActService,
        private toastyService: ToastService,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.title = this.translateService.instant(marker('Manual Item Correction'));
        this.form = this.createFormGroup(this.cargoTypeId);
        this.loadItem();
    }

    ngAfterContentChecked(): void {
        this.cdr.detectChanges();
    }

    save(): void {
        if (!this.form.valid || this.isBusy) {
            return;
        }

        this.isBusy = true;

        const payload = {
            id: this.id,
            version: this.item?.version,
            approvedCustomsApprovalOverrideConfirmed: this.approvedCustomsApprovalOverrideConfirmed,
            ...this.form.getRawValue()
        };
        this.generalActService.saveGeneralActLockedItemManualCorrection(payload).subscribe({
            next: (id: number) => {
                this.toastyService.success(
                    this.translateService.instant(marker('Manual item correction saved successfully'))
                );

                this.activeModal.close({ result: true, id });
            },
            error: (error) => {
                this.isBusy = false;
            },
            complete: () => {
                this.isBusy = false;
            }
        });
    }

    private loadItem(): void {
        this.isBusy = true;

        this.generalActService.getGeneralActLockedItemManualCorrection(this.id).subscribe({
            next: (item) => {
                this.item = item;
                this.form.patchValue({
                    ...item,
                    correctionReason: item.correctionRemark
                });
            },
            error: () => {
                this.isBusy = false;
            },
            complete: () => {
                this.isBusy = false;
            }
        });
    }

    private createFormGroup(cargoTypeId: string): FormGroup {
        const isBulk = cargoTypeId === GeneralActCargoTypeCodes.bulk;
        const requirePort = this.forBulk || this.forRoro;

        return new FormGroup({
            billOfLading: new FormControl(null, isBulk ? maxLengthValidator(50) : []),
            loadingDischargePortId: new FormControl(null, requirePort ? [requiredValidator()] : []),
            description: new FormControl(null),
            consignee: new FormControl(null),
            vehicleTypeId: new FormControl(null),
            declaredQuantity: new FormControl(null),
            declaredWeight: new FormControl(null),
            declaredWeightInVacuum: new FormControl(null),
            declaredVolume: new FormControl(null),
            realizedQuantity: new FormControl(null),
            realizedWeight: new FormControl(null),
            realizedWeightInVacuum: new FormControl(null),
            realizedVolume: new FormControl(null),
            declaredTotalCont: new FormControl(null),
            declaredFull20: new FormControl(null),
            declaredFull40: new FormControl(null),
            declaredEmpty20: new FormControl(null),
            declaredEmpty40: new FormControl(null),
            realizedTotalCont: new FormControl(null),
            realizedFull20: new FormControl(null),
            realizedFull40: new FormControl(null),
            realizedEmpty20: new FormControl(null),
            realizedEmpty40: new FormControl(null),
            declaredTotalVehicles: new FormControl(null),
            realizedTotalVehicles: new FormControl(null),
            correctionReason: new FormControl(null, [requiredValidator(), maxLengthValidator(1275)])
        });
    }
}
